import { createLogic } from 'redux-logic'
import { LogicDeps } from '@chegg-wt/core/state'
import {
  CREATE_FOLDER_MODAL,
  DELETE_MODAL,
  EDIT_FOLDER_MODAL,
  MOVE_PROJECT_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import { createSuccessAction } from '../app/actions'
import { closeModal } from '../modals/actions'
import { storeProjects, fetchProjectsError } from '../projects/actions'
import {
  FetchFolderProjectsPayload,
  AddFolderPayload,
  RemoveFolderPayload,
  EditFolderPayload,
  MoveProjectsPayload,
} from './types'
import { folderSelector } from './selectors'
import { ActionTypes } from './constants'
import {
  setFolders,
  setFolderId,
  fetchFolderProjects,
  addFolderFailed,
  editFolderFailed,
  removeFoldersFailed,
} from './actions'

export const fetchFoldersLogic = createLogic<any, never, any, LogicDeps>({
  type: ActionTypes.FETCH_ALL_FOLDERS,
  latest: true,
  processOptions: {
    successType: ActionTypes.SET_FOLDERS,
    failType: ActionTypes.FETCH_FOLDERS_ERROR,
  },
  process({ api }) {
    return api.bff.getFolders()
  },
})

export const fetchFolderProjectsLogic = createLogic<
  any,
  FetchFolderProjectsPayload,
  any,
  LogicDeps
>({
  type: ActionTypes.FETCH_FOLDER_PROJECTS,
  latest: true,
  async process({ api, action }) {
    try {
      const { folderId } = action.payload
      const projects = await api.bff.getProjectsByFolder({ folderId })

      return storeProjects(projects)
    } catch (error) {
      return fetchProjectsError(error)
    }
  },
})

export const createFolderLogic = createLogic<any, AddFolderPayload, any, LogicDeps>({
  type: ActionTypes.ADD_FOLDERS_REQUEST,
  async process({ api, action }, dispatch, done) {
    try {
      await api.bff.createFolder(action.payload)

      // TODO(team): Reconsider slim reducers pattern? Further abstract modals redux-logic flows?
      const folders = await api.bff.getFolders()
      dispatch(setFolders(folders))

      dispatch(closeModal(CREATE_FOLDER_MODAL))
    } catch (error) {
      // TODO: Error handling and messaging
      dispatch(addFolderFailed(error))
    } finally {
      done()
    }
  },
})

export const updateFolderLogic = createLogic<any, EditFolderPayload, any, LogicDeps>({
  type: ActionTypes.EDIT_FOLDERS_REQUEST,
  async process({ api, action }, dispatch, done) {
    try {
      const { folder } = action.payload
      await api.bff.editFolder(folder)

      // TODO(team): Reconsider slim reducers pattern? Further abstract modals redux-logic flows?
      const folders = await api.bff.getFolders()
      dispatch(setFolders(folders))

      dispatch(closeModal(EDIT_FOLDER_MODAL))
    } catch (error) {
      // TODO: Error handling and messaging
      dispatch(editFolderFailed(error))
    } finally {
      done()
    }
  },
})

export const deleteFolderLogic = createLogic<any, RemoveFolderPayload, any, LogicDeps>({
  type: ActionTypes.REMOVE_FOLDERS_REQUEST,
  async process({ api, action, getState }, dispatch, done) {
    try {
      const oldFolder = folderSelector(getState(), action.payload)
      await api.bff.deleteFolder({ id: oldFolder.id })

      dispatch(closeModal(DELETE_MODAL))

      // TODO(team): Reconsider slim reducers pattern? Further abstract modals redux-logic flows?
      const folders = await api.bff.getFolders()
      dispatch(setFolders(folders))

      // TODO: What message should I have here? How should the message be displayed?
      dispatch(createSuccessAction(ActionTypes.REMOVE_FOLDERS_SUCCESS)({ msg: '' }))
    } catch (error) {
      dispatch(removeFoldersFailed(error))
    } finally {
      done()
    }
  },
})

export const moveProjectsToFolderLogic = createLogic<
  any,
  MoveProjectsPayload,
  any,
  LogicDeps
>({
  type: ActionTypes.MOVE_PROJECTS_REQUEST,

  async process({ api, action }, dispatch, done) {
    try {
      const { folderId, projectIds } = action.payload

      await api.bff.moveProjectsToFolder({ folderId, projectIds })

      dispatch(setFolderId(folderId))

      dispatch(fetchFolderProjects(folderId))

      dispatch(closeModal(MOVE_PROJECT_MODAL))
    } catch (error) {
      dispatch(fetchProjectsError(error))
    } finally {
      done()
    }
  },
})
